import { Add } from '@mui/icons-material';
import { Skeleton } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { CREATE_WORKSPACE } from '../../constants/routes';
import { GET_USER_WORKSPACES, GET_WORKSPACE_INVITES_BY_EMAIL } from '../../apis/workspace';
import { navigateToMarketingSite } from '../../utils/navigator';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/atoms/button';
import LogoutDialog from '../../components/Logout';
import OnboardImage from '../../images/onboard-image.svg';
import OnboardingHeader from './onboarding-header';
import useLoader from '../../context/LoaderContext';
import WorkspaceInvitationItem from './workspace-invitation-item';
import WorkspaceItem from './workspace-item';

const NoWorkspacesMessage = () => (
  <div className="no-workspace-messages">
    <p className="no-workspace-message">No workspace / invitation found! </p>
    <p className="no-workspace-message">Please get started by creating a new workspace!</p>
  </div>
);

const WorkspaceInvitations = ({ invitations }) => (
  <div className="workspace-invitations">
    <p className="workspace-invitation-title">Accept an invitation</p>
    <p className="workspace-invitation-description">These are workspaces you have been invited to join.</p>
    {invitations.map((invitation, index) => (
      <WorkspaceInvitationItem key={`${invitation.workspaceName}-${index}`} invitation={invitation} isLastItem={index === invitations.length - 1} />
    ))}
  </div>
);

const WorkspaceList = ({ workspaces }) => (
  <div className="workspace-invitations">
    <p className="workspace-invitation-title">Launch a workspace</p>
    <p className="workspace-invitation-description">These are workspaces you have joined</p>
    {workspaces.map((workspace, index) => (
      <WorkspaceItem key={`${workspace.workspaceName}-${index}`} workspace={workspace} isLastItem={index === workspaces.length - 1} />
    ))}
  </div>
);

const UserOnboarding = () => {
  // init
  const { isLoading, setIsLoading } = useLoader();
  const { authUser } = useAuth();
  const navigate = useNavigate();

  // states
  const [workSpaces, setWorkspaces] = useState([]);
  const [invites, setInvites] = useState([]);
  const [isOpenLogoutDialog, setIsOpenLogoutDialog] = useState(false);

  // mutations/queries
  const [getWorkspaces] = useLazyQuery(GET_USER_WORKSPACES, { fetchPolicy: 'no-cache' });
  const [getWorkspaceInvites] = useLazyQuery(GET_WORKSPACE_INVITES_BY_EMAIL, { fetchPolicy: 'no-cache' });

  const handleDialog = () => setIsOpenLogoutDialog(!isOpenLogoutDialog);

  const handleCreateWorkspace = () => navigate(`/${CREATE_WORKSPACE}`);

  useEffect(() => {
    const fetchInvitesAndWorkspaces = async () => {
      try {
        if (!isLoading) setIsLoading(true);
        const [workspacesData, invitesData] = await Promise.all([getWorkspaces({ variables: { userId: authUser.id } }), getWorkspaceInvites({ variables: { email: authUser.email } })]);
        if (workspacesData && workspacesData.data && workspacesData.data.listWorkspace) setWorkspaces(workspacesData.data.listWorkspace);
        if (invitesData && invitesData.data && invitesData.data.listWorkspaceInviteByEmail) setInvites(invitesData.data.listWorkspaceInviteByEmail);
      } finally {
        setIsLoading(false);
      }
    };
    if (authUser) fetchInvitesAndWorkspaces();
  }, [getWorkspaces, getWorkspaceInvites, authUser]);

  return (
    <section className="onboarding-container">
      <OnboardingHeader />
      <div className="onboarding-create-workspace">
        <div className="onboarding-greetings">
          <span className="onboarding-welcome-text">Welcome!</span>
          <span className="onboarding-create-workspace-text">Create or access your workspaces</span>
        </div>
        <Button primary onClick={handleCreateWorkspace}>
          <Add />
          <strong>&nbsp;Create Workspace</strong>
        </Button>
      </div>
      <div className="workspaces-outer-card">
        <div className="workspaces-inner-card">
          <div className="logout-container">
            <div className="email-text"><span>Workspaces for&nbsp;</span>{authUser?.email ?? <Skeleton component="span" animation="wave" variant="text" width={150} height={20} />}</div>
            <LogoutDialog
              handleDialog={handleDialog}
              openDialog={isOpenLogoutDialog}
              component={<Button primary small outlinedColor onClick={handleDialog}><strong>Change</strong></Button>}
              setOpenDialog={setIsOpenLogoutDialog}
              onLogoutSuccess={navigateToMarketingSite}
            />
          </div>
          {!!invites.length && <WorkspaceInvitations invitations={invites} />}
          {!!workSpaces.length && <WorkspaceList workspaces={workSpaces} />}
          {!isLoading && !workSpaces.length && !invites.length && <NoWorkspacesMessage />}
        </div>
        <img alt="Onboarding" src={OnboardImage} className="onboard-image" />
      </div>
      <div className="copyright">ⓒ 2024 TechHolding LLC</div>
    </section>
  );
};

WorkspaceList.propTypes = {
  workspaces: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      userCount: PropTypes.number.isRequired,
      workspaceLogo: PropTypes.string,
      handle: PropTypes.string.isRequired,
      logo: PropTypes.string,
      isDisabled: PropTypes.bool,
    }),
  ).isRequired,
};

WorkspaceInvitations.propTypes = {
  invitations: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string.isRequired,
      createdByEmail: PropTypes.string.isRequired,
      workspaceMetadata: PropTypes.shape({
        name: PropTypes.string,
        logo: PropTypes.string,
        workspaceMembers: PropTypes.number,
      }),
    }),
  ).isRequired,
};
export default UserOnboarding;
